import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Layers, Trophy, UsersRound, CalendarClock, CalendarDays, CalendarRange, ChevronLeft, ChevronRight, ClipboardList, Loader2, NotebookPen, Sun } from "lucide-react";
import { fetchPlan } from "@/lib/bf";
import BFAgenda from "./BFAgenda";
import BFPlanAdd, { BFPlanDel } from "./BFPlanAdd";
import BFRouteNotes from "./BFRouteNotes";
import BFVisitDay from "./BFVisitDay";
import BFNewClient from "./BFNewClient";
import BFMySalons from "./BFMySalons";
import { BFTiles } from "./BFTiles";

const iso = (d) => d.toLocaleDateString("sv-SE");
const today = () => new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Bucharest" }));
const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const add = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const WD = ["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"];

const Salon = ({ x, done, onDone }) => (
  <span className="bf-plan-salon-wrap">
    <Link to={`/client/${x.id}`} className={`bf-plan-salon ${done ? "is-done" : x.missed ? "is-missed" : ""} ${x.item ? "is-manual" : ""}`} title={x.note || ""} data-testid={`bf-plan-salon-${x.id}`}>{x.time ? `${x.time} ` : ""}{x.salon || "Salon"}</Link>
    {x.item && onDone && <BFPlanDel x={x} onDone={onDone} />}
  </span>
);
const Items = ({ d, max = 99, onDone }) => {
  const all = [...d.visited.map((x) => [x, true]), ...d.planned.map((x) => [x, false])];
  return <>{all.slice(0, max).map(([x, done]) => <Salon key={`${done ? "v" : "p"}${x.item || x.id}`} x={x} done={done} onDone={max === 99 ? onDone : null} />)}{all.length > max && <small>+{all.length - max}</small>}</>;
};

const Week = ({ start }) => {
  const [p, setP] = useState(null);
  const [rev, setRev] = useState(0);
  const re = () => setRev((n) => n + 1);
  useEffect(() => { window.addEventListener("bf-plan-changed", re); return () => window.removeEventListener("bf-plan-changed", re); }, []);
  useEffect(() => { fetchPlan(iso(start), 7).then(setP).catch(() => setP({ days: [] })); }, [start, rev]);
  if (!p) return <p className="st-hint"><Loader2 size={14} className="animate-spin" /> Se încarcă…</p>;
  const t = iso(today());
  return (
    <div className="bf-plan-week" data-testid="bf-plan-week">
      {p.days.map((d, i) => (
        <div key={d.date} className={`bf-plan-day ${d.date === t ? "is-today" : ""}`} data-testid={`bf-plan-day-${d.date}`}>
          <b>{WD[i]} {new Date(`${d.date}T12:00:00`).getDate()}</b>
          <small>{d.planned.length} planificate · {d.visited.length} vizitate</small>
          <Items d={d} onDone={re} />
          {!d.planned.length && !d.visited.length && <em>liber</em>}
          {d.date >= t && <BFPlanAdd day={d.date} onDone={re} />}
        </div>
      ))}
    </div>
  );
};

const Month = ({ first }) => {
  const [p, setP] = useState(null);
  const [sel, setSel] = useState("");
  const [rev, setRev] = useState(0);
  const re = () => setRev((x) => x + 1);
  useEffect(() => { window.addEventListener("bf-plan-changed", re); return () => window.removeEventListener("bf-plan-changed", re); }, []);
  const n = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  useEffect(() => { setSel(""); }, [first]);
  useEffect(() => { fetchPlan(iso(first), n).then(setP).catch(() => setP({ days: [] })); }, [first, n, rev]);
  if (!p) return <p className="st-hint"><Loader2 size={14} className="animate-spin" /> Se încarcă…</p>;
  const pad = (first.getDay() + 6) % 7;
  const t = iso(today());
  const tot = p.days.reduce((s, d) => [s[0] + d.planned.filter((x) => !x.missed).length, s[1] + d.visited.length, s[2] + d.planned.filter((x) => x.missed).length], [0, 0, 0]);
  const day = p.days.find((d) => d.date === sel);
  return (
    <>
      <p className="st-hint" data-testid="bf-plan-month-sum">{tot[0]} vizite de făcut · {tot[1]} vizite făcute{tot[2] ? ` · ${tot[2]} ratate` : ""} în luna aceasta</p>
      <div className="bf-plan-month" data-testid="bf-plan-month">
        {WD.map((w) => <span key={w} className="bf-plan-wd">{w}</span>)}
        {Array.from({ length: pad }, (_, i) => <span key={`e${i}`} />)}
        {p.days.map((d) => (
          <div key={d.date} role="button" tabIndex={0} onClick={() => setSel(d.date)} onKeyDown={(e) => e.key === "Enter" && setSel(d.date)} className={`bf-plan-cell ${d.date === t ? "is-today" : ""} ${d.date === sel ? "is-sel" : ""}`} data-testid={`bf-plan-cell-${d.date}`}>
            <b>{new Date(`${d.date}T12:00:00`).getDate()}</b>
            <div className="bf-plan-cell-names"><Items d={d} max={3} /></div>
            {d.visited.length + d.planned.length > 0 && <span className="bf-plan-cell-dots"><i className="is-v">{d.visited.length || ""}</i><i>{d.planned.length || ""}</i></span>}
          </div>
        ))}
      </div>
      {day && (
        <div className="bf-plan-day bf-plan-picked" data-testid="bf-plan-picked">
          <b>{new Date(`${day.date}T12:00:00`).toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long" })}</b>
          <Items d={day} onDone={re} />
          {!day.planned.length && !day.visited.length && <em>liber</em>}
          {day.date >= t && <BFPlanAdd day={day.date} onDone={re} />}
        </div>
      )}
    </>
  );
};

const PLAN_TILES = [
  { id: "luna", label: "Luna mea", hint: "Toate vizitele lunii, pe zile", icon: CalendarDays },
  { id: "sapt", label: "Săptămâna mea", hint: "Luni–duminică, cu programări", icon: CalendarRange },
  { id: "zi", label: "Ziua mea", hint: "Vizitele, zilele de naștere și saloanele de azi", icon: Sun },
  { id: "obs", label: "Observații din rută", hint: "Ce ai discutat la fiecare salon, direct în fișă", icon: NotebookPen },
  { id: "fise", label: "Fișe clienți", hint: "Fișa fiecărui salon, client nou", icon: ClipboardList },
  { id: "schimba", label: "Schimbă ziua de vizită", hint: "Mută ziua fixă a unui salon", icon: CalendarClock },
].map((s) => ({ ...s, img: `/agent-tiles/plan-${s.id}-3d.jpg` })).concat([
  { id: "clienti", label: "Clienții mei", hint: "Saloanele mele în Excel, contracte de trimis", icon: UsersRound },
  { id: "categorii", label: "Categorii clienți", hint: "Clienți A, B, C: muți tu sau se mută automat după comenzi", icon: Layers },
  { id: "rezultate", label: "Rezultatele mele", hint: "Saloane înscrise, comenzi, locul tău între agenți", icon: Trophy },
].map((s) => ({ ...s, img: `/agent-tiles/${s.id}-v2.jpg` })));

const Fise = () => (
  <section className="invite-box bf-plan-fise" data-testid="bf-plan-fise">
    <h3><ClipboardList size={18} /> Fișe clienți</h3>
    <p className="st-hint">Fișa fiecărui salon: vizite, firmă, servicii, branduri, poze, comenzi. Vizitele programate și observațiile din fișă apar automat în planificare.</p>
    <BFNewClient /><div className="share-actions"><BFMySalons /></div>
  </section>
);

export default function BFAgentPlan({ tab: t = "", onTab, extra = {} }) {
  const tab = PLAN_TILES.some((x) => x.id === t) ? t : "";
  const setTab = onTab;
  const [wk, setWk] = useState(() => monday(today()));
  const [mo, setMo] = useState(() => { const d = today(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const cal = ["luna", "sapt"].includes(tab);
  if (!tab) return <div data-testid="bf-agent-plan"><BFTiles badges={{}} onOpen={setTab} sections={PLAN_TILES} /></div>;
  return (
    <div data-testid="bf-agent-plan">
      <div className="bf-plan-head">
        <button type="button" className="st-btn st-btn--ghost" onClick={() => setTab("")} data-testid="bf-plan-back"><ArrowLeft size={14} /> Cadranele planificării</button>
        <nav className="bfa-sub-nav">{PLAN_TILES.map((x) => <button key={x.id} type="button" className={tab === x.id ? "is-on" : ""} onClick={() => setTab(x.id)} data-testid={`bf-plan-tab-${x.id}`}>{x.label}</button>)}</nav>
      </div>
      {cal && (
        <div className="bf-plan-nav">
          <button type="button" className="st-btn st-btn--ghost" onClick={() => (tab === "sapt" ? setWk(add(wk, -7)) : setMo(new Date(mo.getFullYear(), mo.getMonth() - 1, 1)))} data-testid="bf-plan-prev"><ChevronLeft size={16} /></button>
          <b data-testid="bf-plan-title">{tab === "sapt" ? `${wk.toLocaleDateString("ro-RO", { day: "numeric", month: "short" })} – ${add(wk, 6).toLocaleDateString("ro-RO", { day: "numeric", month: "short", year: "numeric" })}` : mo.toLocaleDateString("ro-RO", { month: "long", year: "numeric" })}</b>
          <button type="button" className="st-btn st-btn--ghost" onClick={() => (tab === "sapt" ? setWk(add(wk, 7)) : setMo(new Date(mo.getFullYear(), mo.getMonth() + 1, 1)))} data-testid="bf-plan-next"><ChevronRight size={16} /></button>
        </div>
      )}
      {tab === "zi" && <BFAgenda empty />}
      {tab === "sapt" && <Week start={wk} />}
      {tab === "luna" && <Month first={mo} />}
      {tab === "obs" && <BFRouteNotes />}
      {tab === "fise" && <Fise />}
      {tab === "schimba" && <BFVisitDay />}
      {extra[tab]}
      {cal && <p className="st-hint">Planificarea vine din „Ziua de vizită” și „Cât de des” din fișa fiecărui client. Verde = vizită notată, gri = vizită ratată, auriu = vizită programată de tine (cu „Programează” pe orice zi).</p>}
    </div>
  );
}
